"use client";

import { useCallback, useEffect, useState } from "react";

import {
  ColorMode,
  CookiePreferences,
  createCookiePreferences,
  getCookiePreferences,
  saveCookiePreferences,
} from "@/utils/cookiePreferences";

const COLOR_SCHEME_MEDIA_QUERY = "(prefers-color-scheme: dark)";

export function useCookiePreferenceState(
  defaultCookiePreferences: CookiePreferences,
) {
  const [isDark, setIsDark] = useState(false);
  const [isCookiesAllowed, setIsCookiesAllowed] = useState(true);
  const [cookiePreferences, setCookiePreferences] = useState(
    defaultCookiePreferences,
  );
  const [hasCookiePreference, setHasCookiePreference] = useState(true);
  const [isCookiePreferenceLoaded, setIsCookiePreferenceLoaded] =
    useState(false);
  const [isCookiePreferenceEditorOpen, setIsCookiePreferenceEditorOpen] =
    useState(false);

  useEffect(() => {
    const preferences = getCookiePreferences();
    const prefersDark = window.matchMedia(COLOR_SCHEME_MEDIA_QUERY).matches;
    const shouldUseDark = preferences?.colorMode
      ? preferences.colorMode === "dark"
      : prefersDark;
    const nextPreferences =
      preferences ??
      createCookiePreferences({
        analyticalCookies: true,
        colorMode: shouldUseDark ? "dark" : "light",
        hasCookiePreference: false,
        technicalCookies: true,
      });

    setCookiePreferences(nextPreferences);
    setIsCookiesAllowed(nextPreferences.isCookiesAllowed);
    setHasCookiePreference(nextPreferences.hasCookiePreference);

    document.documentElement.classList.toggle("dark", shouldUseDark);
    document.documentElement.dataset.theme = shouldUseDark ? "dark" : "light";
    document.documentElement.style.colorScheme = shouldUseDark
      ? "dark"
      : "light";
    setIsDark(shouldUseDark);
    setIsCookiePreferenceLoaded(true);
  }, []);

  const setColorMode = useCallback((mode: ColorMode) => {
    const nextIsDark = mode === "dark";
    document.documentElement.classList.toggle("dark", nextIsDark);
    document.documentElement.dataset.theme = mode;
    document.documentElement.style.colorScheme = mode;
    setIsDark(nextIsDark);
    setCookiePreferences((current) => {
      const nextPreferences = { ...current, colorMode: mode };
      saveCookiePreferences(nextPreferences);
      return nextPreferences;
    });
  }, []);

  const toggleTheme = useCallback(() => {
    setColorMode(isDark ? "light" : "dark");
  }, [isDark, setColorMode]);

  const setCookiePreference = useCallback(
    (preferences: CookiePreferences) => {
      const nextPreferences = {
        ...preferences,
        colorMode: preferences.colorMode ?? (isDark ? "dark" : "light"),
        hasCookiePreference: true,
      };

      saveCookiePreferences(nextPreferences);
      setCookiePreferences(nextPreferences);
      setIsCookiesAllowed(nextPreferences.isCookiesAllowed);
      setHasCookiePreference(true);
      setIsCookiePreferenceLoaded(true);
      setIsCookiePreferenceEditorOpen(false);
    },
    [isDark],
  );

  const openCookiePreferences = useCallback(() => {
    setIsCookiePreferenceLoaded(true);
    setIsCookiePreferenceEditorOpen(true);
  }, []);

  return {
    cookiePreferences,
    hasCookiePreference,
    isCookiePreferenceEditorOpen,
    isCookiePreferenceLoaded,
    isCookiesAllowed,
    isDark,
    openCookiePreferences,
    setCookiePreference,
    setColorMode,
    toggleTheme,
  };
}
